
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Project, Associate } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PlusIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);
const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>);

const ToggleSwitch: React.FC<{ enabled: boolean, onChange: () => void }> = ({ enabled, onChange }) => (
    <button
        type="button"
        className={`${enabled ? 'bg-gray-800' : 'bg-gray-300'} relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
        onClick={(e) => { e.stopPropagation(); onChange(); }}
        aria-checked={enabled}
        role="switch"
    >
        <span className={`${enabled ? 'translate-x-6' : 'translate-x-1'} inline-block w-4 h-4 transform bg-white rounded-full transition-transform`} />
    </button>
);

const SortableProjectItem: React.FC<{ project: Project, onTogglePublished: (project: Project) => void, onToggleFeatured: (project: Project) => void, onDelete: (project: Project) => void }> = ({ project, onTogglePublished, onToggleFeatured, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: project.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <tr ref={setNodeRef} style={style}>
            <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm w-12">
                <button {...attributes} {...listeners} className="cursor-grab p-2 text-gray-400 hover:text-gray-700">
                    <MenuIcon className="h-5 w-5" />
                </button>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <Link to={`/admin/project/edit/${project.id}`} className="text-gray-900 hover:text-indigo-600 whitespace-no-wrap font-semibold">
                    {project.title}
                </Link>
                <p className="text-gray-600 whitespace-no-wrap text-xs">{project.year}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap">{project.category}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap">{project.status}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center">
                <ToggleSwitch enabled={project.published} onChange={() => onTogglePublished(project)} />
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center">
                <ToggleSwitch enabled={project.featured} onChange={() => onToggleFeatured(project)} />
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                <Link to={`/admin/project/edit/${project.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                    Edit
                </Link>
                <button onClick={() => onDelete(project)} className="text-red-600 hover:text-red-900">
                    Delete
                </button>
            </td>
        </tr>
    );
};


const AdminDashboardPage: React.FC = () => {
    const { data: projects, setData: setProjects, isLoading } = useCollection<Project>('projects', {field: 'order', direction: 'asc'});
    const { data: associates } = useCollection<Associate>('associates');
    const [searchQuery, setSearchQuery] = useState('');
    const [yearFilter, setYearFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [associateFilter, setAssociateFilter] = useState('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (project: Project) => {
        setProjectToDelete(project);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!projectToDelete) return;
        
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'projects', projectToDelete.id));
            setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
            showToast('Project deleted successfully.', { type: 'success' });
        } catch (error) {
            console.error("Failed to delete project:", error);
            showToast("Error: Could not delete the project.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setProjectToDelete(null);
            setIsDeleting(false);
        }
    };
    
    const handleUpdate = async (project: Project) => {
        const projectRef = doc(db, 'projects', project.id);
        await updateDoc(projectRef, project);
        setProjects(prev => prev.map(p => p.id === project.id ? project : p));
    }
    
    const handleTogglePublished = (project: Project) => {
        handleUpdate({ ...project, published: !project.published });
    }
    
    const handleToggleFeatured = (project: Project) => {
        handleUpdate({ ...project, featured: !project.featured });
    }

    const { years, categories } = useMemo(() => {
        if (!projects) return { years: [], categories: [] };
        const yearSet = new Set<number>();
        const categorySet = new Set<string>();
        projects.forEach(p => {
            yearSet.add(p.year);
            categorySet.add(p.category);
        });
        return {
            years: Array.from(yearSet).sort((a,b) => b-a),
            categories: Array.from(categorySet).sort(),
        }
    }, [projects]);

    const filteredProjects = useMemo(() => {
        if (!projects) return [];
        return projects.filter(project => {
            const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesYear = yearFilter === 'all' || project.year === parseInt(yearFilter);
            const matchesCategory = categoryFilter === 'all' || project.category === categoryFilter;
            const matchesAssociate = associateFilter === 'all'
                ? true
                : associateFilter === 'none'
                ? !project.associateSlug
                : project.associateSlug === associateFilter;
            return matchesSearch && matchesYear && matchesCategory && matchesAssociate;
        });
    }, [projects, searchQuery, yearFilter, categoryFilter, associateFilter]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (filteredProjects && over && active.id !== over.id) {
            const oldIndex = filteredProjects.findIndex((p) => p.id === active.id);
            const newIndex = filteredProjects.findIndex((p) => p.id === over.id);
            const newOrderProjects = arrayMove(filteredProjects, oldIndex, newIndex);
            
            setProjects(newOrderProjects); // Optimistic update

            try {
                const batch = writeBatch(db);
                newOrderProjects.forEach((project, index) => {
                    const projectRef = doc(db, 'projects', project.id);
                    batch.update(projectRef, { order: index });
                });
                await batch.commit();
                showToast('Project order updated.', { type: 'success' });
            } catch (error) {
                showToast('Failed to update project order.', { type: 'error' });
                // Revert optimistic update on failure
                setProjects(projects);
            }
        }
    };

    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Project"
                description={`Are you sure you want to delete the project "${projectToDelete?.title}"? This action cannot be undone.`}
                isConfirming={isDeleting}
            />

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Projects</h2>
                <Link to="/admin/project/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                   <PlusIcon className="h-5 w-5 mr-2"/>
                    Add New Project
                </Link>
            </div>

            {/* Filters */}
            <div className="mb-4 bg-white p-4 rounded-lg shadow-md flex flex-wrap items-center gap-4">
                <div className="flex-grow">
                    <label htmlFor="search" className="sr-only">Search Projects</label>
                    <input 
                        type="text" 
                        id="search"
                        placeholder="Search by title..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                    />
                </div>
                <div>
                     <label htmlFor="year-filter" className="sr-only">Filter by Year</label>
                     <select 
                        id="year-filter"
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}
                         className="px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                     >
                        <option value="all">All Years</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                     </select>
                </div>
                 <div>
                     <label htmlFor="category-filter" className="sr-only">Filter by Category</label>
                     <select 
                        id="category-filter"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                     >
                        <option value="all">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                     </select>
                </div>
                 <div>
                     <label htmlFor="associate-filter" className="sr-only">Filter by Associate</label>
                     <select 
                        id="associate-filter"
                        value={associateFilter}
                        onChange={(e) => setAssociateFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                     >
                        <option value="all">All Associates</option>
                        <option value="none">None (Main Firm)</option>
                        {(associates || []).map(assoc => (
                            <option key={assoc.id} value={assoc.slug}>{assoc.name}</option>
                        ))}
                     </select>
                </div>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading projects...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 w-12"></th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Project Title
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Category
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Published
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Featured
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                         <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={filteredProjects.map(p => p.id)} strategy={verticalListSortingStrategy}>
                                <tbody>
                                    {filteredProjects.length > 0 ? filteredProjects.map(project => (
                                        <SortableProjectItem 
                                            key={project.id} 
                                            project={project}
                                            onTogglePublished={handleTogglePublished}
                                            onToggleFeatured={handleToggleFeatured}
                                            onDelete={openDeleteModal}
                                        />
                                    )) : (
                                        <tr>
                                            <td colSpan={7} className="text-center py-10 text-gray-500">
                                                No projects match the current filters.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </SortableContext>
                        </DndContext>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdminDashboardPage;
